import React, { useEffect, useMemo, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  useGetDairyCowTypes,
  useGetFootprintingDairyHerdDetails,
  useGetFootprintingDairyHerdSize,
  useMutateFootprintingDairyHerdDetails,
  useMutateFootprintingDairyHerdSize,
  useTranslation,
} from "utils";

import { FootprintingDairyHerdSize, UploadableHerdSize } from "models";

import { Card, Flex, Form, FormInstance, InputImageCard, PageSpin, Typography } from "components";

interface CowTypeSelectionScreenProps {
  year: number;
  farmId: string;
  primaryDataId?: string;
  existingHerdSize?: FootprintingDairyHerdSize[];
  onCountsChange?: (counts: Record<string, number | null>) => void;
  onComplete?: () => void;
  readOnly?: boolean;
}

const CowTypeSelectionScreen = ({ primaryDataId, farmId, year, existingHerdSize = [], onCountsChange, readOnly }: CowTypeSelectionScreenProps) => {
  const { t } = useTranslation(["footprintingDairy", "dairy"]);
  const [cowCounts, setCowCounts] = useState<Record<string, number | null>>({});

  const cowCountsRef = useRef(cowCounts);
  const existingHerdRef = useRef(existingHerdSize);

  useEffect(() => {
    cowCountsRef.current = cowCounts;
  }, [cowCounts]);

  useEffect(() => {
    existingHerdRef.current = existingHerdSize;
  }, [existingHerdSize]);

  const { data: cowTypes, isLoading: cowTypesLoading } = useGetDairyCowTypes({ enabled: true });
  const { mutate: saveHerdSize } = useMutateFootprintingDairyHerdSize(primaryDataId || "", farmId, year);

  const isLoading = cowTypesLoading;

  useEffect(
    () => () => {
      if (!readOnly && !isLoading && primaryDataId) {
        const herdData = Object.entries(cowCountsRef.current)
          .filter(([_, count]) => count !== null && count !== undefined)
          .map(([cowTypeId, cowCount]) => {
            const existing = existingHerdRef.current.find((h) => h.cowTypeId === cowTypeId);
            return {
              cowTypeId,
              cowCount: cowCount,
              ...(existing && { id: existing.id }),
            };
          });

        if (herdData.length > 0 || existingHerdRef.current.length > 0) {
          saveHerdSize(herdData as UploadableHerdSize[]);
        }
      }
    },
    [isLoading, saveHerdSize, readOnly, primaryDataId]
  );

  useEffect(() => {
    if (existingHerdSize && existingHerdSize.length > 0) {
      const initialCounts = existingHerdSize.reduce(
        (acc, herd) => {
          acc[herd.cowTypeId] = herd.cowCount;
          return acc;
        },
        {} as Record<string, number>
      );
      setCowCounts(initialCounts);
      onCountsChange?.(initialCounts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingHerdSize]);

  const handleCowCountChange = (cowTypeId: string, value: number | null) => {
    const newCounts = {
      ...cowCounts,
      [cowTypeId]: value,
    };
    setCowCounts(newCounts);
    onCountsChange?.(newCounts);
  };

  if (cowTypesLoading) {
    return <PageSpin />;
  }

  if (!cowTypes || cowTypes.length === 0) {
    return null;
  }

  return (
    <Flex vertical align="center" gap={16} className="margin">
      {cowTypes.map((cowType) => (
        <InputImageCard
          key={cowType.id}
          title={t(`dairy:cowTypes.${cowType.translationSlug}`)}
          onChange={(value) => handleCowCountChange(cowType.id, value)}
          image={{
            imageSize: 48,
            imageAlt: `${cowType.name} image`,
            imageSrc: `https://klimpublic.s3.eu-central-1.amazonaws.com/${cowType.translationSlug}.png`,
          }}
          value={cowCounts[cowType.id] ?? null}
          disabled={readOnly}
          suffix={t("dairyHerdSizeForm.cowsSuffix")}
        />
      ))}
    </Flex>
  );
};

const HerdSizeForm = ({ primaryDataId, readOnly, farmId, year }: CowTypeSelectionScreenProps) => {
  const { t } = useTranslation(["footprintingDairy", "dairy"]);
  const formRef = useRef<FormInstance>(null);
  const queryClient = useQueryClient();

  const [calvesAmount, setCalvesAmount] = useState<number | null>(null);
  const [cowCounts, setCowCounts] = useState<Record<string, number | null>>({});

  const { data: herdDetails, isLoading } = useGetFootprintingDairyHerdDetails(primaryDataId ?? "", {
    enabled: !!primaryDataId,
  });

  const { data: existingHerdSize } = useGetFootprintingDairyHerdSize(primaryDataId || "", {
    enabled: !!primaryDataId,
  });

  const { mutate: mutateHerdDetails } = useMutateFootprintingDairyHerdDetails(
    farmId,
    year,
    {
      calvesAmount,
    },
    primaryDataId
  );

  const totalHerdSize = useMemo(() => {
    if (!cowCounts) return 0;
    const cowTotal = Object.values(cowCounts).reduce<number>((sum, count) => sum + (count ?? 0), 0);
    return cowTotal + (calvesAmount ?? 0);
  }, [cowCounts, calvesAmount]);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading && primaryDataId) {
        mutateHerdDetails();
        queryClient.invalidateQueries({ queryKey: ["footprintingDairyHerdSize", primaryDataId] });
      }
    },
    [isLoading, mutateHerdDetails, primaryDataId, readOnly, queryClient]
  );

  useEffect(() => {
    if (herdDetails) {
      setCalvesAmount(herdDetails.calvesAmount ?? null);
      formRef.current?.setFieldsValue({
        calvesAmount: herdDetails.calvesAmount ?? null,
      });

      const fieldsToValidate = herdDetails.calvesAmount !== null && herdDetails.calvesAmount !== undefined;

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [herdDetails]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Flex vertical gap={16}>
      <Card border dataTestId="footprinting-dairy-herd-size" className="padding-200">
        <Flex vertical gap={16}>
          <Flex vertical gap={8}>
            <Typography variant="h4">{t("dairyHerdSizeForm.herdSizeQuestion")}</Typography>
            <Typography variant="paragraph-m" color="grey-600">
              {t("dairyHerdSizeForm.herdSizeQuestionDescription")}
            </Typography>
          </Flex>
          <Form.Item>
            <CowTypeSelectionScreen
              year={year}
              farmId={farmId}
              primaryDataId={primaryDataId || ""}
              existingHerdSize={existingHerdSize || []}
              onCountsChange={setCowCounts}
              readOnly={readOnly}
            />
          </Form.Item>
        </Flex>
      </Card>

      <Card border className="padding-200">
        <Form ref={formRef}>
          <Flex vertical gap={16}>
            <Flex vertical gap={8}>
              <Typography variant="h4">{t("dairyHerdSizeForm.herdSizeAmountOfCalvesQuestion")}</Typography>
              <Typography variant="paragraph-m" color="grey-600">
                {t("dairyHerdSizeForm.herdSizeAmountOfCalvesQuestionDescription")}
              </Typography>
            </Flex>
            <Form.Item>
              <InputImageCard
                title={t("dairyHerdSizeForm.question.herdSizeAmountOfCalvesInput")}
                onChange={(value) => setCalvesAmount(value)}
                image={{
                  imageSize: 48,
                  imageAlt: "Calves image",
                  imageSrc: "https://klimpublic.s3.eu-central-1.amazonaws.com/calve.png",
                }}
                value={calvesAmount ?? null}
                disabled={readOnly}
                suffix={t("dairyHerdSizeForm.calvesSuffix")}
              />
            </Form.Item>
          </Flex>
        </Form>
      </Card>

      <Card border className="padding-200">
        <Flex className="dairy-display-information-card flex-center padding-100">
          <Typography variant="paragraph-s" color="grey-600" className="margin-bottom-25">
            {t("dairyHerdSizeForm.herdSizeDisplayTitle")}
          </Typography>
          <Typography variant="h5" className="center">
            {totalHerdSize} {t("dairyHerdSizeForm.herdSizeDisplayUnit")}
          </Typography>
        </Flex>
      </Card>
    </Flex>
  );
};

HerdSizeForm.Key = "dairy.herdSize";
export { HerdSizeForm };
